/**
 * Output section setup: section 05 of the controls sidebar.
 *
 * Builds the hidden OUTPUT section containing the export-scale picker,
 * the Export PNG button, and the Copy PNG button. The section is kept in
 * the DOM (hidden=true) so the export-scale select is accessible to
 * main.ts; the Export / Copy buttons live in the top-bar overlay.
 *
 * The returned refs are consumed by the parent createControls closure in:
 *   - syncI18n:      exportScale.select option-text refresh on locale change
 *   - return value:  exportScaleSelect, exportButton, copyButton exposed
 *                    via ControlsHandles
 *
 * Extracted from createControls (CSS-04.3): public API of createControls
 * unchanged; no behavioural change.
 */

// ---------------------------------------------------------------------------
// Inline type aliases matching the private interfaces in controls.ts.
// ---------------------------------------------------------------------------

interface SectionGroup {
  section: HTMLElement;
  body: HTMLDivElement;
  head: HTMLHeadingElement;
}

interface SelectRow {
  row: HTMLDivElement;
  select: HTMLSelectElement;
  root: HTMLDivElement;
  refresh: () => void;
}

interface SelectRowOptions<T extends string> {
  labelKey: string;
  tipKey?: string;
  ctrlId: string;
  options: ReadonlyArray<readonly [T, string]>;
  value: T;
}

/** Dependencies injected by createControls so this module can use the
 *  private helper functions without duplicating their implementations. */
export interface OutputSectionDeps {
  /** The `.controls` aside element; the section is appended here. */
  root: HTMLElement;
  makeSection: (
    ix: string,
    titleKey: string,
    variant?: "" | "violet" | "yellow",
  ) => SectionGroup;
  buildSelectRow: <T extends string>(opts: SelectRowOptions<T>) => SelectRow;
}

// ---------------------------------------------------------------------------
// Refs returned to the parent closure
// ---------------------------------------------------------------------------

/** Handles that syncI18n and the createControls return value need from the
 *  output section. */
export interface OutputSectionRefs {
  /** Export-scale picker (1x/2x/4x); main.ts reads `.value` for scale. */
  exportScale: SelectRow;
  /** Primary export button - Export PNG (violet variant). */
  exportButton: HTMLButtonElement;
  /** Secondary copy button - Copy PNG to clipboard. */
  copyButton: HTMLButtonElement;
}

// ---------------------------------------------------------------------------
// Setup function
// ---------------------------------------------------------------------------

/**
 * Builds and attaches the OUTPUT section (section 05) to `deps.root`.
 * The section is hidden in the sidebar; its controls are surfaced via the
 * top-bar overlay. Returns the element refs required by the parent closure.
 */
export function setupOutputSection(deps: OutputSectionDeps): OutputSectionRefs {
  const { root, makeSection, buildSelectRow } = deps;

  // ========== 05 OUTPUT (Item 12: moved to top-bar Export dropdown) ==========
  // The section is kept in the DOM so exportScaleSelect is accessible, but
  // hidden from the sidebar since the Export button lives in the top bar.
  // Renumbered from 06 to 05 after I3 removed section 04.
  const outputSection = makeSection("05", "label.output");
  outputSection.section.hidden = true;
  root.appendChild(outputSection.section);

  // Export-scale picker (relocated from the topbar - sitting it next to
  // the Export PNG / Copy PNG buttons keeps "what scale am I exporting?"
  // co-located with "save / copy".
  const exportScale = buildSelectRow({
    labelKey: "export.scale",
    ctrlId: "ctrl-exportScale",
    options: [
      ["1", "export.scale.1x"],
      ["2", "export.scale.2x"],
      ["4", "export.scale.4x"],
    ] as const,
    value: "1",
  });
  outputSection.body.appendChild(exportScale.row);

  const outputCluster = document.createElement("div");
  outputCluster.className = "output-cluster";
  outputCluster.style.display = "grid";
  outputCluster.style.gap = "8px";
  outputSection.body.appendChild(outputCluster);

  const exportButton = document.createElement("button");
  exportButton.type = "button";
  exportButton.className = "btn btn-violet";
  exportButton.style.justifyContent = "center";
  exportButton.style.width = "100%";
  exportButton.dataset["i18n"] = "action.export";
  exportButton.disabled = true;
  outputCluster.appendChild(exportButton);

  const copyButton = document.createElement("button");
  copyButton.type = "button";
  copyButton.className = "btn";
  copyButton.style.justifyContent = "center";
  copyButton.style.width = "100%";
  copyButton.dataset["i18n"] = "action.copy";
  copyButton.disabled = true;
  outputCluster.appendChild(copyButton);

  return { exportScale, exportButton, copyButton };
}
